"use client";

import { useMemo, useState, useEffect } from "react";
import { type StreakStatusResponse, AUTOMATIC_STREAK_STATUS } from "@cede/types";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getStatusConfig,
  getStopReasonLabel,
  getNextTradeTime,
  getEpochStartTime,
  isStreakActive,
  isStreakCancellable,
  formatStreakDate,
  getLastTradeError,
  getFailedTradeIndex,
  formatCountdown,
} from "@/utils/automatic-streak";
import {
  CheckCircle,
  Clock,
  Pause,
  Ban,
  AlertTriangle,
  Loader2,
  TrendingUp,
  DollarSign,
  Timer,
  CalendarClock,
  ShieldCheck,
  Info,
} from "lucide-react";

interface AutomaticStreakProgressProps {
  streak: StreakStatusResponse;
  onCancel?: () => void;
  isCancelling?: boolean;
  className?: string;
  /** When true, removes the container border/background (useful when rendered inside a modal) */
  bare?: boolean;
  /** Current epoch ID - used to show "Next Epoch" indicator */
  currentEpochId?: number;
  /** Spread threshold percentage from campaign config (default: 2%) */
  spreadThreshold?: number | null;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  [AUTOMATIC_STREAK_STATUS.PENDING]: <Clock className="w-5 h-5" />,
  [AUTOMATIC_STREAK_STATUS.IN_PROGRESS]: <Loader2 className="w-5 h-5 animate-spin" />,
  [AUTOMATIC_STREAK_STATUS.COMPLETED]: <CheckCircle className="w-5 h-5" />,
  [AUTOMATIC_STREAK_STATUS.STOPPED]: <Pause className="w-5 h-5" />,
  [AUTOMATIC_STREAK_STATUS.CANCELLED]: <Ban className="w-5 h-5" />,
};

const DEFAULT_ICON = <AlertTriangle className="w-5 h-5" />;

/**
 * Hook to create a countdown timer that updates every second
 * Returns seconds remaining until target time
 */
const useSecondsUntil = (targetTime: Date | null): number | null => {
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!targetTime) {
      setSecondsRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const target = targetTime.getTime();
      const diff = Math.max(0, Math.ceil((target - now) / 1000));
      setSecondsRemaining(diff);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetTime]);

  return secondsRemaining;
};

const DEFAULT_SPREAD_THRESHOLD = 2;

export const AutomaticStreakProgress = ({
  streak,
  onCancel,
  isCancelling = false,
  className,
  bare = false,
  currentEpochId,
  spreadThreshold,
}: AutomaticStreakProgressProps) => {
  const displaySpreadThreshold = spreadThreshold ?? DEFAULT_SPREAD_THRESHOLD;
  const statusConfig = getStatusConfig(streak.status);
  const isNextEpoch = currentEpochId !== undefined && streak.epochId > currentEpochId;

  // Progress calculation based on completed cycles
  const progress = useMemo(() => {
    if (streak.totalTrades === 0) return 0;
    const totalProgress = streak.completedTrades + (streak.skippedCycles ?? 0);
    return (totalProgress / streak.totalTrades) * 100;
  }, [streak.completedTrades, streak.skippedCycles, streak.totalTrades]);

  // Calculate epoch start time (for "Next epoch starts in" countdown)
  const epochStartTime = useMemo(() => getEpochStartTime(streak), [streak]);
  const secondsUntilEpochStart = useSecondsUntil(epochStartTime);

  // Calculate next trade time countdown (for "First cycle in" / "Next cycle in")
  const nextTradeTime = useMemo(() => getNextTradeTime(streak), [streak]);
  const secondsUntilNextTrade = useSecondsUntil(nextTradeTime);

  // Use epoch start time for "Next epoch starts in", trade time for other labels
  const countdownSeconds = isNextEpoch ? secondsUntilEpochStart : secondsUntilNextTrade;

  const canCancel = isStreakCancellable(streak.status);
  const isActive = isStreakActive(streak.status);
  const statusIcon = STATUS_ICONS[streak.status] ?? DEFAULT_ICON;

  return (
    <div
      className={cn(bare ? "" : "p-4 rounded-lg border border-cede-border-container bg-cede-bg-component", className)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", statusConfig.bgColor, statusConfig.color)}>{statusIcon}</div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-cede-text-primary">{streak.pairSymbol}</h3>
              {isNextEpoch && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cede-primary/20 text-cede-primary flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" />
                  Next Epoch
                </span>
              )}
            </div>
            <span className={cn("text-sm", statusConfig.color)}>{statusConfig.label}</span>
          </div>
        </div>

        {canCancel && onCancel && (
          <Button variant="secondary" size="small" onClick={onCancel} disabled={isCancelling}>
            {isCancelling ? "Cancelling..." : "Cancel"}
          </Button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-cede-text-secondary">Progress</span>
          <span className="text-cede-text-primary">
            {streak.completedTrades} / {streak.totalTrades} cycles
            {(streak.skippedCycles ?? 0) > 0 && (
              <span className="text-cede-warning ml-1">({streak.skippedCycles} skipped)</span>
            )}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-cede-text-secondary" />
          <div>
            <p className="text-xs text-cede-text-secondary">Volume Traded</p>
            <p className="text-sm text-cede-text-primary">${parseFloat(streak.totalVolumeTraded).toFixed(2)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {countdownSeconds !== null && countdownSeconds > 0 ? (
            <>
              {isNextEpoch ? (
                <CalendarClock className="w-4 h-4 text-cede-primary" />
              ) : (
                <Timer className="w-4 h-4 text-cede-primary" />
              )}
              <div>
                <p className="text-xs text-cede-text-secondary">
                  {isNextEpoch
                    ? "Next epoch starts in"
                    : streak.completedTrades === 0
                      ? "First cycle in"
                      : "Next cycle in"}
                </p>
                <p className="text-sm text-cede-primary font-medium">{formatCountdown(countdownSeconds)}</p>
              </div>
            </>
          ) : countdownSeconds === 0 ? (
            <>
              <Loader2 className="w-4 h-4 text-cede-primary animate-spin" />
              <div>
                <p className="text-xs text-cede-text-secondary">Trade</p>
                <p className="text-sm text-cede-primary font-medium">Executing...</p>
              </div>
            </>
          ) : (
            <>
              <TrendingUp className="w-4 h-4 text-cede-text-secondary" />
              <div>
                <p className="text-xs text-cede-text-secondary">Interval</p>
                <p className="text-sm text-cede-text-primary">{streak.intervalSeconds}s</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Time Info & Configuration */}
      <div className="text-xs text-cede-text-tertiary space-y-1">
        {/* First trade time - show when first trade has been executed */}
        {streak.executionLog?.trades?.[0]?.timestamp && (
          <div className="flex justify-between">
            <span>First trade at:</span>
            <span>{formatStreakDate(streak.executionLog.trades[0].timestamp)}</span>
          </div>
        )}
        {streak.finishedAt && (
          <div className="flex justify-between">
            <span>Finished:</span>
            <span>{formatStreakDate(streak.finishedAt)}</span>
          </div>
        )}
        {/* V1 Configuration */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            Volume per cycle:
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Each cycle executes a buy + sell trade. A random amount between $
                  {parseFloat(streak.volumeMin).toFixed(2)} and ${parseFloat(streak.volumeMax).toFixed(2)} is used for
                  each trade, so the total is doubled.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span>
            ${(parseFloat(streak.volumeMin) * 2).toFixed(2)} - ${(parseFloat(streak.volumeMax) * 2).toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Interval:</span>
          <span>{streak.intervalSeconds}s between cycles</span>
        </div>
        {/* Spread Protection Info */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-cede-success" />
            Spread protection:
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Cycles are automatically skipped when the bid-ask spread exceeds {displaySpreadThreshold}%. This
                  protects you from unfavorable trades during high volatility or low liquidity periods.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-cede-success">{`<${displaySpreadThreshold}%`}</span>
        </div>
        {/* Show skipped cycles count if any */}
        {(streak.skippedCycles ?? 0) > 0 && (
          <div className="flex justify-between items-center text-cede-warning">
            <span className="flex items-center gap-1">
              Cycles skipped:
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-cede-warning/70 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  <p>
                    These cycles were skipped because the bid-ask spread exceeded {displaySpreadThreshold}% at execution
                    time. This protects you from unfavorable trades.
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <span>{streak.skippedCycles}</span>
          </div>
        )}
        {/* Volume Protection Info */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-cede-success" />
            Volume protection:
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]">
                <p>
                  Autopilot automatically stops when you reach your epoch volume limit. This prevents unnecessary trades
                  and protects you from paying extra fees.
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-cede-success">Active</span>
        </div>
      </div>

      {/* Stop Reason */}
      {streak.stopReason && (
        <div className="mt-4 p-3 rounded-lg bg-cede-error/10 border border-cede-error/20">
          <p className="text-sm text-cede-error flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              {getStopReasonLabel(streak.stopReason)}
              {/* Show which trade failed */}
              {getFailedTradeIndex(streak.executionLog) && (
                <span className="text-cede-error/70">
                  {" "}
                  on trade {getFailedTradeIndex(streak.executionLog)}/{streak.totalTrades}
                </span>
              )}
            </span>
          </p>
          {/* Show actual trade error message if available */}
          {getLastTradeError(streak.executionLog) && (
            <p className="text-xs text-cede-error/80 mt-1.5 ml-6 italic">
              &ldquo;{getLastTradeError(streak.executionLog)}&rdquo;
            </p>
          )}
        </div>
      )}

      {/* Active Indicator */}
      {isActive && (
        <div className="mt-4 flex items-center gap-2 text-xs text-cede-text-secondary">
          <div className="w-2 h-2 rounded-full bg-cede-success animate-pulse" />
          <span>Auto-updating</span>
        </div>
      )}
    </div>
  );
};
